import Button from '@/components/Button'
import CardConfigure from '@/componentsDS/Modal/CardConfigure'
import Modal from '@/componentsDS/Modal/Modal'
import googleKeys from '@/constants/configuration/google-keys.json'
import useModal from '@/hooks/use-modal'
import { LayoutType } from '@/layoutDS/layout'
import Container from '@/layouts/index'
import { Navigate, Route, SwitchRoutes, useLocation, useNavigate } from '@/lib/router-dom'
import { authGuard } from '@/middlewares/auth'
import { dexGuard } from '@/middlewares/dex-guard'
import { redirectGuard } from '@/middlewares/redirect'
import { tfaGuard } from '@/middlewares/tfa-guard'
import { versionGuard } from '@/middlewares/version'
import ApprovalTransactionSuccessPage from '@/pages/approvals/approvals-transaction-success.jsx'
import ApprovalTransaction from '@/pages/approvals/approvals-transaction.jsx'
import ApprovalsPage from '@/pages/approvals/index.jsx'
import AuditTrailPage from '@/pages/audit-trail.jsx'
import AuthRedirect from '@/pages/authRedirect.jsx'
import ApiConfiguration from '@/pages/configuration/api-configuration.jsx'
import DataSettings from '@/pages/configuration/data-settings.jsx'
import LicenseKeys from '@/pages/configuration/license-key.jsx'
import PitStopSourceSystem from '@/pages/configuration/pitstop-sourcesystem.jsx'
import DashboardPage from '@/pages/dashboard/index.jsx'
import DataDictionaryPage from '@/pages/data-dictionary/index.jsx'
import ShareDataDownloadPage from '@/pages/download.jsx'
import CreateEclipForm from '@/pages/eclip/create-eclip-form.jsx'
import CreateEclipSend from '@/pages/eclip/create-eclip-send.jsx'
import FolderPackagePage from '@/pages/eclip/folder-package.jsx'
import EclipHomePage from '@/pages/eclip/index.jsx'
import PackageCreatePage from '@/pages/eclip/package-create.jsx'
import PackageDataElementPayloadPage from '@/pages/eclip/package-data-element-payload.jsx'
import PackageDetailsPage from '@/pages/eclip/package-details.jsx'
import PackageHomePage from '@/pages/eclip/package-index.jsx'
import RequestPackageForm from '@/pages/eclip/request-package-form.jsx'
import EFormPage from '@/pages/eform/index.jsx'
import PackageEformPage from '@/pages/eform/package-eform.jsx'
import Forward from '@/pages/forward/index.jsx'
import ForwardSuccessPage from '@/pages/forward/success.jsx'
import Installation from '@/pages/installation.jsx'
import Login from '@/pages/login.jsx'
import RequestFailPage from '@/pages/received-data/fail.jsx'
import ReceivedDataPage from '@/pages/received-data/index.jsx'
import RequestPage from '@/pages/received-data/request.jsx'
import RequestSuccessPage from '@/pages/received-data/success.jsx'
import EndorsementChainPage from '@/pages/record-transfer-endorsement-chain/index.jsx'
import RecordTransferDetailsPage from '@/pages/record-transfer/etr-issuance/record-transfer-details.jsx'
import RecordTransferShareSuccessPage from '@/pages/record-transfer/etr-issuance/record-transfer-share-success.jsx'
import RecordTransferSharePage from '@/pages/record-transfer/etr-issuance/record-transfer-share.jsx'
import RecordTransferPage from '@/pages/record-transfer/index.jsx'
import RecordTransferSuccessPage from '@/pages/record-transfer/success.jsx'
import ActivateTfa from '@/pages/setting-up-tfa.jsx'
import SettingPage from '@/pages/setting/index.jsx'
import EformSharePage from '@/pages/shared-data/eform-share.jsx'
import ShareDataFailPage from '@/pages/shared-data/fail.jsx'
import SharedDataPage from '@/pages/shared-data/index.jsx'
import ShareDataSuccessPage from '@/pages/shared-data/success.jsx'
import SimulateProvide from '@/pages/simulate-provide.jsx'
import SimulateReceive from '@/pages/simulate-receive.jsx'
import SoftwareUpdate from '@/pages/software-update.jsx'
import TransactionUpdate from '@/pages/transaction-update.jsx'
import { logout, renewToken } from '@/redux/actions/auth.js'
import rem from '@/utils/rem'
import googleTagManager from '@analytics/google-tag-manager'
import Analytics from 'analytics'
import axios from 'axios'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import ReactGA from 'react-ga'
import { useDispatch, useSelector } from 'react-redux'
import Portal from './components/Portal/index'
import ToastMini from './components/ToastMini/index'
import Tooltip from './components/Tooltip/Tooltip'
import { DEX_TYPE, userRole } from './config/constants'
import { ToastContext } from './context/toast/index'
import { useBuildex } from './hooks/use-buildex/index'
import useFavicon from './hooks/use-favicon/index'
import RequestPackagePreview from './pages/eclip/request-package-preview.jsx'
import versionReduxAction from './redux/actions/version'
import { getEnvAndDex } from './utils/get-env-and-dex'
import useTheme from './hooks/use-theme/index'
import { useFeature } from './hooks/use-feature/index'
import { featureConstant } from './constants/configuration/feature'
import MasterDataManagementPage from './pages/master-data-management/index.jsx'
import MasterDataManagementDetailsPage from './pages/master-data-management/details.jsx'
import MasterDataManagementUploadPage from './pages/master-data-management/upload.jsx'

const { dex, env } = getEnvAndDex();

let analytics = null;

if (googleKeys?.[dex]?.[env]?.GOOGLE_GTM_ID) {
	analytics = Analytics({
		app: 'Admin Portal',
		plugins: [
			googleTagManager({
				containerId: `${googleKeys?.[dex]?.[env]?.GOOGLE_GTM_ID}`
			})
		]
	})
}

let popupTimer = null;
let logoutTimer = null;
let renewTokenTimer = null;

function App() {

	const dispatch = useDispatch()
	const [toastList, setToastList] = useState([])

	const [loading, setLoading] = useState(false);

	const {  profile } = useSelector(state => state.auth)

	useFavicon();

	function onLoadingScreenClicked(event) {
		event.stopPropagation();
	}

	function addToastMessage(message, type = 'success', action = null) {
		const id = Math.floor((Math.random() * 101) + 1);

		if (toastList.length > 2) {
			toastList.splice(0, 1)
		}

		setToastList([...toastList, { id, type, message, action }])
	}

	// Global toast message context
	const toastContext = {
		toastList,
		setToastList,
		addToastMessage
	}

	// useWebsocket()

	const { goTo } = useNavigate()
	const isLoggedIn = useSelector(state => state.auth.loggedIn)

	const { visible: visiblePopup, show: showPopup, hide: hidePopup } = useModal()

	const [logoutTime, setLogoutTime] = useState("")

	if (googleKeys?.[dex]?.[env]?.GOOGLE_GTM_ID) {
		analytics.page();
	}

	useTheme();
	const isEmbedDashboard =useFeature(featureConstant.DASHBOARD_EMBEDDING)

	useEffect(() => {
		if (googleKeys?.[dex]?.[env]?.GOOGLE_TRACKING_ID) {
			ReactGA.initialize(`${googleKeys?.[dex]?.[env]?.GOOGLE_TRACKING_ID}`);
		}

		if (isLoggedIn) {
			window.addEventListener('click', addTimer);
			window.addEventListener('keydown', addTimer);
			window.addEventListener('scroll', addTimer); // for mouse scrolling
			window.addEventListener('mousemove', addTimer); // for mouse moving
			window.addEventListener('dashboard-activity', addTimer);
			addTimer()

			const nextRenew = Number(sessionStorage.getItem(RENEW_TOKEN_KEY));
			const now = Date.now();
			if (nextRenew && nextRenew > now) {
				renewTokenTimer = setTimeout(() => {
					dispatch(renewToken());
					setRenewTokenTimer(dispatch);
				}, nextRenew - now);
			} else {
				setRenewTokenTimer(dispatch);
			}
		}
		if (!isLoggedIn) {
			window.removeEventListener('click', addTimer);
			window.removeEventListener('keydown', addTimer);
			window.removeEventListener('scroll', addTimer);
			window.removeEventListener('mousemove', addTimer);
			window.removeEventListener('dashboard-activity', addTimer);
			hidePopup()
			clearTimeout(popupTimer)
			clearTimeout(logoutTimer)
			// goTo('/login')
			clearTimeout(renewTokenTimer);
		}

		return () => {
			clearInterval(renewTokenTimer);
		};
	}, [isLoggedIn])

	const isEtrUser = useMemo(() => {
		return profile?.role?.some((role) => role.name === userRole.ETR_USER)
	}, [profile])

	function addTimer() {
		clearTimeout(popupTimer)
		clearTimeout(logoutTimer)

		setLogoutTime(moment().add(15, 'minutes').format('hh:mm A'))

		popupTimer = setTimeout(() => {
			showPopup()
		}, 600_000)

		logoutTimer = setTimeout(() => {
			hidePopup()
			window.onclick = () => { }
			dispatch(logout(goTo));
		}, 900_000)

	}

	const RENEW_TOKEN_KEY = "nextRenewTokenTime";

	function setRenewTokenTimer(dispatch) {
		const now = Date.now();
		const nextRenew = now + 720_000; 
		sessionStorage.setItem(RENEW_TOKEN_KEY, nextRenew);

		renewTokenTimer = setTimeout(() => {
			dispatch(renewToken());
			setRenewTokenTimer(dispatch);
		}, nextRenew - now);
	}

	const isBuildex = useBuildex();

	const getDefaultPage = () => isBuildex ? DashboardPage : SharedDataPage

	return (
		<>
			<div className="App" id={dex}>
				<ToastContext.Provider value={toastContext}>
					<SwitchRoutes>
						<Route path='/' element={<GuardedRoute component={getDefaultPage()} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[versionGuard, authGuard, redirectGuard]} />} />
						<Route path='/login' element={<GuardedRoute component={Login} layout={LayoutType.Login} meta={{ auth: true, dispatch }} guards={[versionGuard, authGuard, tfaGuard, redirectGuard]} />} />
						<Route path='/installation' element={<GuardedRoute component={Installation} layout={LayoutType.Login} meta={{ auth: true, dispatch }} guards={[versionGuard]} />} />
						<Route path='/auth/redirect' element={<GuardedRoute component={AuthRedirect} layout={LayoutType.Login} meta={{ auth: true, dispatch }} />} />

						<Route path='/data-dictionary' element={<GuardedRoute component={DataDictionaryPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/my-activities' element={<GuardedRoute component={AuditTrailPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/setup-tfa' element={<GuardedRoute component={ActivateTfa} layout={LayoutType.Login} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/software-update' element={<GuardedRoute component={SoftwareUpdate} layout={LayoutType.Login} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />

						<Route path='/received-data' element={<GuardedRoute component={ReceivedDataPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/received-data/download' element={<GuardedRoute component={ShareDataDownloadPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/received-data/request' element={<GuardedRoute component={RequestPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/received-data/request/fail' element={<GuardedRoute component={RequestFailPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/received-data/request/success' element={<GuardedRoute component={RequestSuccessPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/received-data/simulate-receive' element={<GuardedRoute component={SimulateReceive} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/received-data/transaction/:messageId' element={<GuardedRoute component={TransactionUpdate} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/received-data/transaction/:messageId/forward' element={<GuardedRoute component={Forward} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path={!isEmbedDashboard? '/dashboard' : '/dashboard/:tabId'} element={<GuardedRoute component={DashboardPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path={!isEmbedDashboard ? '/dashboard' : '/master-data-management'} element={<GuardedRoute component={MasterDataManagementPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path={!isEmbedDashboard ? '/dashboard' : '/master-data-management/details/:id'} element={<GuardedRoute component={MasterDataManagementDetailsPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path={!isEmbedDashboard ? '/dashboard' : '/master-data-management/update/:id'} element={<GuardedRoute component={MasterDataManagementUploadPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/shared-data' element={<GuardedRoute component={SharedDataPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/shared-data/share' element={<GuardedRoute component={EformSharePage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/shared-data/share/fail' element={<GuardedRoute component={ShareDataFailPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/shared-data/share/success' element={<GuardedRoute component={ShareDataSuccessPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/shared-data/simulate-provide' element={<GuardedRoute component={SimulateProvide} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/shared-data/transaction/:messageId' element={<GuardedRoute component={TransactionUpdate} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/shared-data/transaction/:messageId/forward' element={<GuardedRoute component={Forward} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/shared-data/eform' element={<GuardedRoute component={EFormPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />

						<Route path='/forward/success' element={<GuardedRoute component={ForwardSuccessPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />

						<Route path='/configuration/api-configuration' element={<GuardedRoute component={ApiConfiguration} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/configuration/api-configuration/pitstop-sourcesystem' element={<GuardedRoute component={PitStopSourceSystem} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/configuration/data-settings' element={<GuardedRoute component={DataSettings} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/configuration/license-keys' element={<GuardedRoute component={LicenseKeys} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard,dexGuard, tfaGuard, versionGuard]} />} />

						<Route path='/setting' element={<GuardedRoute component={SettingPage} setLoading={setLoading} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/setting/account' element={<GuardedRoute component={SettingPage} setLoading={setLoading} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/setting/notification' element={<GuardedRoute component={SettingPage} setLoading={setLoading} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />

						<Route path='/eclip' element={<GuardedRoute component={EclipHomePage} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/eclip/create' element={<GuardedRoute component={CreateEclipForm} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/eclip/folder/:folderId/:type' element={<GuardedRoute component={EclipHomePage} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/eclip/request' element={<GuardedRoute component={RequestPackageForm} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/eclip/respond/:eclipId' element={<GuardedRoute component={RequestPackagePreview} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/eclip/send/:eclipId' element={<GuardedRoute component={CreateEclipSend} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/eclip/send/:eclipId/import/:dataElementId/:type' element={<GuardedRoute component={CreateEclipSend} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/eclip/send/:eclipId/import/:dataElementId/:type/:messageStoreId' element={<GuardedRoute component={CreateEclipSend} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/eclip/shared' element={<GuardedRoute component={EclipHomePage} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/eclip/templates' element={<GuardedRoute component={EclipHomePage} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/eclip/update/:eclipId/:dataElementId/:type' element={<GuardedRoute component={PackageEformPage} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />

						{/* Revamped Package Sharing Route */}
						<Route path='/package' element={<GuardedRoute component={PackageHomePage} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/package/create' element={<GuardedRoute component={PackageCreatePage} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/package/folder/:folderId' element={<GuardedRoute component={FolderPackagePage} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/package/templates' element={<GuardedRoute component={PackageHomePage} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/package/details/:eclipId' element={<GuardedRoute component={PackageDetailsPage} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/package/details/:eclipId/eform' element={<GuardedRoute component={PackageEformPage} addToastMessage={addToastMessage} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />
						<Route path='/package/data-element/:eclipId/:dataElementId' element={<GuardedRoute component={PackageDataElementPayloadPage} layout={LayoutType.Default} meta={{ auth: true, dispatch, allowedDex: [DEX_TYPE.SGTRADEX] }} guards={[authGuard, tfaGuard, versionGuard, dexGuard]} />} />

						<Route path='/record-transfer' element={<GuardedRoute component={isEtrUser ? RecordTransferPage : getDefaultPage()} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/record-transfer/record-details/:transferableRecordId' element={<GuardedRoute component={isEtrUser ? RecordTransferDetailsPage : getDefaultPage()} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/record-transfer/success' element={<GuardedRoute component={isEtrUser ? RecordTransferSuccessPage : getDefaultPage()} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/record-transfer/share' element={<GuardedRoute component={isEtrUser ? RecordTransferSharePage : getDefaultPage()} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/record-transfer/share/success' element={<GuardedRoute component={isEtrUser ? RecordTransferShareSuccessPage : getDefaultPage()} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/record-transfer/record-details/:transferableRecordId/endorsement-chain' element={<GuardedRoute component={isEtrUser ? EndorsementChainPage : getDefaultPage()} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/approvals' element={<GuardedRoute component={profile?.makerCheckerRole ==='CHECKER' ? ApprovalsPage :getDefaultPage() } layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/approvals/transaction/:transactionRequestId' element={<GuardedRoute component={profile?.makerCheckerRole ==='CHECKER'? ApprovalTransaction : getDefaultPage()} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/approvals/transaction/:transactionRequestId/:transferableRecordId' element={<GuardedRoute component={profile?.makerCheckerRole ==='CHECKER'? ApprovalTransaction : getDefaultPage()} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
						<Route path='/approvals/transaction/success' element={<GuardedRoute component={profile?.makerCheckerRole ==='CHECKER' ? ApprovalTransactionSuccessPage : getDefaultPage()} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, versionGuard]} />} />
					</SwitchRoutes>
					<ToastMini toastList={toastList} />
				</ToastContext.Provider>
			</div>
			<Modal visible={visiblePopup } variant='medium' close={hidePopup} dismissClickOutsideModal={false}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											<span className='inactive-title'>Auto Logout</span>
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="inactive-text">
								Your session has been idle for over 10 minutes. You will be logged out at <span className='inactive-time'>{logoutTime}</span>. Do you want to stay logged in your session?
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Logout"
								submit={() => {
									dispatch(logout(goTo));
								}}
							/>
							<span style={{ width: rem(5), display: 'inline-block' }}></span>
							<Button full maxWidth="140" text="Continue Session"
								submit={async() => {
									try {
									hidePopup(); 
									await dispatch(renewToken());
									addTimer();
								} catch (e) {
									console.error("Error renewing token:", e); }
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Portal id="app-blocker">
				{loading && (
					<div className="modal white" onClick={onLoadingScreenClicked}>
						<div className="loading">
							<img src="/loading.gif" />
						</div>
					</div>
				)}
			</Portal>

			<Tooltip />
		</>
	)
}

const useGuards = (guards, meta) => {
	const location = useLocation();

	const metaInformation = {
		...meta,
		location
	}

	if (!guards) return null;

	for (let guard of guards) {
		const redirectPath = guard(metaInformation);
		if (redirectPath) {
			return redirectPath;
		}
	}
	return null;
};

function GuardedRoute({ component: Component, layout, guards, meta, setLoading, additionalProps, ...props }) {
	const location = useLocation();
	const redirectPath = useGuards(guards, meta);
	const { goTo } = useNavigate();

	const dispatch = meta.dispatch;

	if (redirectPath && location.currentPath !== redirectPath) {
		return <Navigate to={redirectPath} replace={true} />;
	}

	function successResponse(response) {
		const versionInfo = response?.data?.version;
		if (versionInfo) {
			dispatch(versionReduxAction(versionInfo, goTo));
		}
		return response;
	}

	function errorResponse(error) {
		const versionInfo = error?.response?.data?.version;
		if (versionInfo) {
			dispatch(versionReduxAction(versionInfo, goTo));
		}
		console.log("errorResponse", error)
		return Promise.reject(error);
	}

	axios.interceptors.response.use(successResponse, errorResponse);

	const RenderedComponent = (
		<Container layout={layout}>
			<Component {...props} {...additionalProps} setLoading={setLoading} />
		</Container>
	);

	return RenderedComponent;
}

export default App
